/**
 * ====================================================================
 * [기준 데이터] 싱글 소스 오브 트루스 (Single Source of Truth)
 * 요금제별 고유 식별자, 명칭, 라우팅 경로, 테마 컬러 등을 정의합니다.
 * ====================================================================
 */
import React from 'react';
import content from 'jsons/content/billing/billingPlan.json';

export const BILLING_PLANS_BASE = {
  free: {
    id: 0,
    payment: 'FREE',
    title: 'FREE',
    kor: '체험판',
    type: 'free',
    money: '무료',
    variant: 'primary',
    target: '개인 / 사업자',
    basePath: '/billing-plan-free',
    infoPath: '/billing-plan-info-free',
  },
  lite: {
    // SMART 100 요금제
    id: 2,
    payment: 'SMART 100',
    title: 'SMART 100',
    kor: '정액제',
    type: 'lite',
    money: '1,000,000원',
    variant: 'yellow500',
    target: '개인 / 사업자',
    basePath: '/billing-plan-lite',
    infoPath: '/billing-plan-info-lite',
  },
  premium: {
    // START 요금제
    id: 1,
    payment: 'START',
    title: 'START',
    kor: '종량제',
    type: 'premium',
    money: '후불요금',
    variant: 'red500',
    target: '개인 / 사업자',
    basePath: '/billing-plan-premium',
    infoPath: '/billing-plan-info-premium',
  },
  license: {
    // ENTERPRISE 요금제
    id: 3,
    payment: 'ENTERPRISE',
    title: 'ENTERPRISE',
    kor: '라이선스',
    type: 'license',
    money: 'ENTERPRISE 요금',
    variant: 'purple500',
    target: '사업자 계정 (별도 신청)',
    basePath: '/billing-plan-license',
    infoPath: '/billing-plan-info-license',
  },
};

/**
 * ====================================================================
 * [UI 데이터] 요금제 가입/신청 프로세스 및 안내 페이지용 리스트
 * ====================================================================
 */
export const bilingInfoListConst = [
  {
    ...BILLING_PLANS_BASE.free,
    desc: content.infoList.free.desc,
    pageDesc: (
      <>
        {content.infoList.free.pageDesc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoList.free.pageDesc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    buttonText: '신청하기',
    path: BILLING_PLANS_BASE.free.basePath,
  },
  {
    ...BILLING_PLANS_BASE.lite,
    desc: content.infoList.lite.desc,
    pageDesc: (
      <>
        {content.infoList.lite.pageDesc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoList.lite.pageDesc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    sub: content.infoList.lite.sub,
    buttonText: '신청하기',
    path: BILLING_PLANS_BASE.lite.basePath,
  },
  {
    ...BILLING_PLANS_BASE.premium,
    desc: content.infoList.premium.desc,
    pageDesc: (
      <>
        {content.infoList.premium.pageDesc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoList.premium.pageDesc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    buttonText: '신청하기',
    path: BILLING_PLANS_BASE.premium.basePath,
  },
  {
    ...BILLING_PLANS_BASE.license,
    desc: content.infoList.license.desc,
    pageDesc: (
      <>
        {content.infoList.license.pageDesc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoList.license.pageDesc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    buttonText: '신청하기',
    path: BILLING_PLANS_BASE.license.basePath,
  },
];

/**
 * [추출 데이터] 요금제 대문자 타이틀 목록 배열
 * 예: ['FREE', 'START', 'SMART 100', 'ENTERPRISE']
 */
export const biilingPaymentTitleConst = bilingInfoListConst.map(
  (el) => el.payment
);

/**
 * ====================================================================
 * [UI 데이터] 요금제 소개/소개서 메인 페이지 카드 뷰용 리스트
 * *기존 순서 유지(SMART 100과 START의 노출 순서가 bilingInfoListConst와 다름)*
 * ====================================================================
 */
export const billingPlanListConst = [
  {
    ...BILLING_PLANS_BASE.free,
    id: 0,
    buttonText: '자세히 보기',
    path: BILLING_PLANS_BASE.free.infoPath,
    desc: (
      <p>
        {content.planList.free.desc.map((segment, idx) =>
          segment.highlight ? (
            <span key={idx} className="highlight">
              {segment.text}
            </span>
          ) : (
            <React.Fragment key={idx}>{segment.text}</React.Fragment>
          )
        )}
      </p>
    ),
  },
  {
    ...BILLING_PLANS_BASE.lite,
    id: 1,
    buttonText: '자세히 보기',
    path: BILLING_PLANS_BASE.lite.infoPath,
    desc: (
      <p>
        {content.planList.lite.desc.map((segment, idx) =>
          segment.highlight ? (
            <span key={idx} className="highlight">
              {segment.text}
            </span>
          ) : (
            <React.Fragment key={idx}>{segment.text}</React.Fragment>
          )
        )}
      </p>
    ),
  },
  {
    ...BILLING_PLANS_BASE.premium,
    id: 2,
    buttonText: '자세히 보기',
    path: BILLING_PLANS_BASE.premium.infoPath,
    desc: (
      <p>
        {content.planList.premium.desc.map((segment, idx) =>
          segment.highlight ? (
            <span key={idx} className="highlight">
              {segment.text}
            </span>
          ) : (
            <React.Fragment key={idx}>{segment.text}</React.Fragment>
          )
        )}
      </p>
    ),
  },
  {
    ...BILLING_PLANS_BASE.license,
    id: 3,
    buttonText: '자세히 보기',
    path: BILLING_PLANS_BASE.license.infoPath,
    desc: (
      <p>
        {content.planList.license.desc.map((segment, idx) =>
          segment.highlight ? (
            <span key={idx} className="highlight">
              {segment.text}
            </span>
          ) : (
            <React.Fragment key={idx}>{segment.text}</React.Fragment>
          )
        )}
      </p>
    ),
  },
];

/**
 * ====================================================================
 * [UI 데이터] 요금제별 상세 안내 및 유의사항(Message) 맵핑 객체
 * ====================================================================
 */
export const billingPlanInfoDescConst = {
  free: {
    title: BILLING_PLANS_BASE.free.title,
    path: BILLING_PLANS_BASE.free.basePath,
    desc: (
      <>
        {content.infoDesc.free.desc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoDesc.free.desc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    message: content.infoDesc.free.message.map((msg, idx) => (
      <React.Fragment key={idx}>
        {msg.split('\n').map((line, subIdx, arr) => (
          <React.Fragment key={subIdx}>
            {line}
            {subIdx < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    )),
  },
  lite: {
    title: BILLING_PLANS_BASE.lite.title,
    path: BILLING_PLANS_BASE.lite.basePath,
    desc: (
      <>
        {content.infoDesc.lite.desc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoDesc.lite.desc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    message: content.infoDesc.lite.message.map((msg, idx) => (
      <React.Fragment key={idx}>
        {msg.split('\n').map((line, subIdx, arr) => (
          <React.Fragment key={subIdx}>
            {line}
            {subIdx < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    )),
  },
  premium: {
    title: BILLING_PLANS_BASE.premium.title,
    path: BILLING_PLANS_BASE.premium.basePath,
    desc: (
      <>
        {content.infoDesc.premium.desc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoDesc.premium.desc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    message: content.infoDesc.premium.message.map((msg, idx) => (
      <React.Fragment key={idx}>
        {msg.split('\n').map((line, subIdx, arr) => (
          <React.Fragment key={subIdx}>
            {line}
            {subIdx < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    )),
  },
  license: {
    title: BILLING_PLANS_BASE.license.title,
    path: BILLING_PLANS_BASE.license.basePath,
    desc: (
      <>
        {content.infoDesc.license.desc.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < content.infoDesc.license.desc.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    ),
    message: content.infoDesc.license.message.map((msg, idx) => (
      <React.Fragment key={idx}>
        {msg.split('\n').map((line, subIdx, arr) => (
          <React.Fragment key={subIdx}>
            {line}
            {subIdx < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    )),
  },
};

/**
 * ====================================================================
 * [UI 데이터] 데이터 테이블 및 차트 매핑용 요금제 이름/타입 리스트
 * ====================================================================
 */
const baseKeys = ['free', 'lite', 'premium', 'license'];
export const billingPriceNameConst = baseKeys.map((key, index) => {
  const plan = BILLING_PLANS_BASE[key];
  return {
    idx: index,
    value: plan.payment,
    dataType: key === 'premium' ? 'start' : key === 'lite' ? 'smart100' : key, // 레거시 API 키 명칭 매핑 유지
    type: plan.payment.replace(' ', ''), // 공백 제거 (예: 'SMART 100' -> 'SMART100')
    variant: plan.variant,
  };
});

// [특이 케이스 추가] BASE에 존재하지 않는 개별 유형 'TRIAL' 수동 결합
billingPriceNameConst.push({
  idx: 4,
  value: 'TRIAL',
  dataType: 'trial',
  type: 'TRIAL',
  variant: 'green',
});
